import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { API_BASE, registerAuthHandlers } from '../api/client';
import { identify } from '../herald';

// Refresh once the access token is inside this window of expiring, as belt
// and braces on top of the shared client's 401 interceptor (§5.3).
const REFRESH_SKEW_SECONDS = 300;

const AuthContext = createContext();

const secondsUntilExpiry = (token) => {
  try {
    return jwtDecode(token).exp - Date.now() / 1000;
  } catch {
    return -1;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // One in-flight refresh at a time. Without this, several components waking up
  // together each fire a refresh, and rotation means all but one get a token
  // that's already been spent — which trips reuse detection and logs the user out.
  const refreshInFlight = useRef(null);

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;

    const stored = localStorage.getItem('refresh_token');
    if (!stored) return null;

    refreshInFlight.current = (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored, platform: 'web' }),
        });
        if (!res.ok) {
          clearSession();
          return null;
        }
        const data = await res.json();
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        setToken(data.access_token);
        return data.access_token;
      } catch {
        // Network blip — keep the existing session rather than logging out.
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();

    return refreshInFlight.current;
  }, [clearSession]);

  // Hand the shared API client (§5.3) our single-flight refresh and the
  // session-lost cleanup, so every apiFetch call site gets 401-retry for free.
  useEffect(() => {
    registerAuthHandlers({ refresh: refreshAccessToken, onSessionLost: clearSession });
    return () => registerAuthHandlers({});
  }, [refreshAccessToken, clearSession]);

  // Restore the session on load.
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      const remaining = secondsUntilExpiry(storedToken);
      if (remaining > REFRESH_SKEW_SECONDS) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } else if (localStorage.getItem('refresh_token')) {
        setUser(JSON.parse(storedUser));
        refreshAccessToken().then((fresh) => {
          if (!fresh) clearSession();
        });
      } else {
        clearSession();
      }
    }
    setLoading(false);
  }, [refreshAccessToken, clearSession]);

  // Tell the Herald's Hall beacon who is playing; untag it on sign-out, and on
  // any lost session (clearSession nulls the user).
  useEffect(() => {
    if (loading) return;
    if (user?.user_id != null) {
      identify(String(user.user_id), user.first_name || null);
    } else {
      identify(null);
    }
  }, [user, loading]);

  // Renew on focus and on a timer, so a tab left open overnight still works.
  useEffect(() => {
    if (!token) return undefined;

    const maybeRefresh = () => {
      const current = localStorage.getItem('token');
      if (current && secondsUntilExpiry(current) < REFRESH_SKEW_SECONDS) {
        refreshAccessToken();
      }
    };

    window.addEventListener('focus', maybeRefresh);
    const timer = setInterval(maybeRefresh, 60_000);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      clearInterval(timer);
    };
  }, [token, refreshAccessToken]);

  const login = (userData, accessToken, refreshToken) => {
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
  };

  const logout = async () => {
    const stored = localStorage.getItem('refresh_token');
    if (stored) {
      // Best-effort server-side revoke; the local session clears either way.
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: stored }),
        });
      } catch {
        /* offline logout still clears the device */
      }
    }
    clearSession();
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated: !!token, login, logout, loading, refreshAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
